/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { BEM, T, useTranslate } from "@adapptio/lib-components";
import {
	createEmptyScope,
	ISchemaDataSourceModel,
	TGenericDataSourceInstance
} from "@adapptio/lib-blueprint";
import { useBlueprintModel } from "../../hooks/useBlueprintModel";
import { InvalidModelNode } from "./InvalidModelNode";
import { PanelNote } from "../Panel";
import { termsEditorInspector } from "../../../terms/inspector";
import { Icon, ICON_SIZE } from "../Icon";
import { ToolbarButton } from "../Toolbar/ToolbarButton";
import { ToolbarCommandButton } from "../Toolbar/ToolbarCommandButton";
import { TInspectorControlComponent } from "./InspectorTypes";
import { FieldGroup } from "../Fields/FieldGroup";
import { Tabs, TABS_STYLE } from "../Tabs";
import { TABBED_CONTAINER_TYPE, TabbedContainer } from "../TabbedContainer";
import { ModelRuntimeContext } from "../../contexts/ModelRuntimeContext";
import { FieldHeader } from "../Fields/FieldHeader";
import { FieldLabel } from "../Fields/FieldLabel";
import { FieldRow } from "../Fields/FieldRow";
import { FIELD_CONTAINER_STYLE, FieldContainer } from "../Fields/FieldContainer";
import { ScopeViewer } from "../ScopeViewer/ScopeViewer";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { Inspector } from "../../../inspector";
import { InspectorTitle } from "./InspectorTitle";
import { REFERENCE_TYPE } from "../../../modules/Core/ReferenceGuide/ReferenceGuideTypes";
import { useCommand } from "../../hooks";
import { InspectEventMap } from "./InspectEventMap";

export const InspectDataSource: TInspectorControlComponent<ISchemaDataSourceModel> = ({ model: _model }) => {
	const modelRuntimeCtx = useContext(ModelRuntimeContext);
	const t = useTranslate();

	const { model } = useBlueprintModel(_model, "dataSource");

	const [ activeTab, setActiveTab ] = useState("props");
	const [ lastDsInstance, setLastDsInstance ] = useState<TGenericDataSourceInstance>(
		() => model?.schema.getLastInstance(model) ?? null
	);

	const tabs = useMemo(
		() => [
			{
				id: "props",
				// @todo Translate
				tooltip: "Options",
				icon: "mdi/tune-variant"
			},
			{
				id: "events",
				icon: "mdi/lightning-bolt",
				tooltip: "Events"
			},
			{
				id: "debug",
				// @todo Translate
				tooltip: "Debug",
				icon: "mdi/bug"
			}
		],
		[]
	);

	const debugScope = useMemo(() => {
		return createEmptyScope(lastDsInstance?.opts, {});
	}, [ lastDsInstance ]);

	const handleDelete = useCallback(() => {
		model.schema.removeSelfFromDataSourceList(model, true, true);
	}, [ model ]);

	useEffect(() => {
		if (!modelRuntimeCtx || !model) {
			return;
		}

		const handleRender = () => setLastDsInstance(model?.schema.getLastInstance(model) ?? null);
		handleRender();

		onEvent(modelRuntimeCtx.onRender, handleRender);

		return () => {
			offEvent(modelRuntimeCtx.onRender, handleRender);
		};
	}, [ modelRuntimeCtx, model ]);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	const dsHandler = model.dsHandler;

	if (!dsHandler) {
		return (
			<PanelNote>
				<p>
					<T domain="editor" term={termsEditorInspector.errors.dataSourceHasInvalidConfiguration} />
				</p>
			</PanelNote>
		);
	}

	const [ duplicateDataSource ] = useCommand("duplicate.dataSource");

	const handleDuplicate = useCallback(() => {
		duplicateDataSource(model);
	}, [ model ]);

	const baseClass = BEM("edt-inspect-panel");
	baseClass.add("m-inspect-ds");

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("header").toClassName()}>
				<div className={baseClass.element("header-info").toClassName()}>
					<span className={baseClass.element("header-info-icon").toClassName()}>
						<Icon name={dsHandler.icon} size={ICON_SIZE.SMALL} />
					</span>
					<span className={baseClass.element("header-info-name").toClassName()}>
						<T domain="editor" term={dsHandler.label} />
					</span>
					<ToolbarButton
						icon="mdi/content-copy"
						tooltip={t("editor", termsEditorInspector.component.duplicate)}
						onClick={handleDuplicate}
					/>
					<ToolbarButton
						icon="mdi/delete"
						tooltip={t("editor", termsEditorInspector.component.delete)}
						onClick={handleDelete}
					/>
					<ToolbarCommandButton
						icon="mdi/help-circle"
						tooltip={t("editor", termsEditorInspector.component.openDocumentation)}
						commandId="referenceGuide.open"
						commandArgs={[
							{
								name: dsHandler.name,
								type: REFERENCE_TYPE.DATASOURCE
							}
						]}
					/>
				</div>

				<InspectorTitle model={model.id} />
			</div>
			<Tabs
				className={baseClass.element("main-tabs").toClassName()}
				items={tabs}
				selectedId={activeTab}
				onSelect={setActiveTab}
				style={TABS_STYLE.COMPACT}
			/>
			<TabbedContainer
				selectedId={activeTab}
				style={TABBED_CONTAINER_TYPE.SLIDE}
				className={baseClass.element("content").toClassName()}
				items={[
					{
						id: "props",
						render: () => (
							<div className={baseClass.element("content-tab").toClassName()}>
								<Inspector label="Common" group>
									<Inspector model={model.opts} />
								</Inspector>
							</div>
						)
					},
					{
						id: "events",
						render: () => (
							<div className={baseClass.element("content-tab").toClassName()}>
								<InspectEventMap model={model.events} depth={0} path={[ "$events" ]} />
							</div>
						)
					},
					{
						id: "debug",
						render: () => (
							<div className={baseClass.element("content-tab").toClassName()}>
								{lastDsInstance ? (
									<>
										<FieldHeader>
											{/* @todo Translate */}
											<FieldLabel>Resolved Options</FieldLabel>
										</FieldHeader>
										<FieldRow>
											<FieldGroup grow={true}>
												<FieldContainer style={FIELD_CONTAINER_STYLE.BORDER}>
													<ScopeViewer
														scope={debugScope}
														defaultLevelsExpanded={2}
													/>
												</FieldContainer>
											</FieldGroup>
										</FieldRow>
									</>
								) : (
									<PanelNote>Data source hasn't been executed yet.</PanelNote>
								)}
							</div>
						)
					}
				]}
			/>
		</div>
	);
};
